import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ArrowLeft } from 'lucide-react';
import { LoginWelcomeBack } from '../components/PuroAnimations';

const LOGO = "/assets/logo-puro.png";
const LOGO_FILTER = { filter: 'brightness(0) saturate(100%) invert(45%) sepia(28%) saturate(520%) hue-rotate(357deg) brightness(0.92)' };

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userName, setUserName] = useState('');
  const [userIsPrivilege, setUserIsPrivilege] = useState(false);
  const [userRole, setUserRole] = useState('client');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      setUserName(result.data?.name || '');
      setUserIsPrivilege(result.data?.is_privilege || false);
      setUserRole(result.data?.role || 'client');
      setShowWelcome(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (showWelcome) {
    return <LoginWelcomeBack name={userName} isPrivilege={userIsPrivilege} onComplete={() => navigate(userRole === 'admin' ? '/coach' : userRole === 'coach' ? '/espace-coach' : '/')} />;
  }

  return (
    <div className="min-h-screen bg-[#F5EFE5] flex flex-col">
      <div className="p-4 sm:p-6">
        <Link to="/" className="inline-flex items-center text-[#7A6455] hover:text-[#C99A55] transition-colors text-sm">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Accueil
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          <Card className="border-[#E9DDCC] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <CardContent className="p-6 sm:p-8">
              <div className="text-center mb-6">
                <img src={LOGO} alt="PURO" className="h-14 w-auto mx-auto mb-4" style={LOGO_FILTER} />
                <h1 className="font-heading font-medium text-2xl text-[#493329]">Connexion</h1>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-[#C76542]/10 border-[#C76542]/20 text-[#C76542]">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#493329] text-sm">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl border-[#E9DDCC] bg-white" placeholder="vous@exemple.com" required data-testid="login-email" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-[#493329] text-sm">Mot de passe</Label>
                    <Link to="/forgot-password" className="text-xs text-[#C99A55] hover:text-[#493329] transition-colors" data-testid="forgot-password-link">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-xl border-[#E9DDCC] bg-white" placeholder="Votre mot de passe" required data-testid="login-password" />
                </div>
                
                <Button type="submit" className="w-full bg-[#C99A55] hover:bg-[#493329] text-white h-12 rounded-xl btn-premium transition-all duration-400"
                  disabled={loading} data-testid="login-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Se connecter'}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <Link to="/register" className="text-sm text-[#7A6455] hover:text-[#C99A55] transition-colors" data-testid="register-link">
                  Pas encore membre ? S'inscrire
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
